import trim from 'lodash/trim';
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useSearchParams } from 'react-router-dom';

import { oryWhoAmI } from '@revelio/auth';
import { validateEmail } from '@revelio/iso-utility';

import { environment } from '../../environment';
import { SurfContext } from '../../surf-context';
import { createUnverifiedUser } from '../utils';
import { isPressSignup } from '../utils/is-press-signup';
import {
  SalesCta,
  signupEmailFieldError,
  signupErrorMessage,
} from '../utils/signup-error-message';
import type { FormValues, FormValuesPassRequired } from './models';
import { PasswordInput } from './password-input';
import {
  LegalFooter,
  SECTION_SPACING,
  ThemeHeading,
  ThemeLink,
  ThemeTagline,
} from './theme/theme';
import { Button, Field, Spinner, Stack, TextInput, uiStyles } from './ui';

export const Step0 = ({
  setActiveStep,
  setFormData,
}: {
  setActiveStep: (step: number) => void;
  setFormData: Dispatch<SetStateAction<FormValues>>;
}) => {
  // Carried onto the login link so `?s=website` and `?theme` survive.
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  const redirect_uri = searchParams.get('redirect_uri');

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>(null);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormValuesPassRequired>();

  const csrfToken = useContext(SurfContext);

  const checkStatus = useCallback(async () => {
    const user = await oryWhoAmI();
    if (user.active) {
      window.location.replace(redirect_uri || environment.PUBLIC_REACT_APP_URL);
    }
  }, [redirect_uri]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const handleSignUp = handleSubmit(
    async ({ name, email, password }: FormValuesPassRequired) => {
      setLoading(true);
      setErrorMessage(null);
      clearErrors('email');

      const createBody: FormValuesPassRequired = {
        name: trim(name),
        email: trim(email),
        password,
        ...(isPressSignup(searchParams) ? { isPress: true } : {}),
      };

      try {
        const { oryCsrf, flowId, revelioId } = await createUnverifiedUser({
          body: createBody,
          csrfToken,
        });

        setFormData({
          ...createBody,
          csrf: oryCsrf,
          flowId,
          revelioId,
        });
        setActiveStep(1);
      } catch (err) {
        console.error(err);
        const emailError = signupEmailFieldError(err);
        if (emailError) {
          setError('email', { type: 'server', message: emailError });
        }
        setErrorMessage(signupErrorMessage(err));
      }

      setLoading(false);
    }
  );

  return (
    <Stack gap={SECTION_SPACING} fullWidth>
      <Stack gap="12px">
        <ThemeHeading>Create account</ThemeHeading>
        <ThemeTagline>
          Already have an account?{' '}
          <ThemeLink href={`/login${search}`}>Log in</ThemeLink>
        </ThemeTagline>
      </Stack>

      <form onSubmit={handleSignUp}>
        <Stack gap="16px">
          <Field error={errors.name?.message}>
            <TextInput
              invalid={!!errors?.name}
              placeholder="First and last name"
              aria-label="First and last name"
              autoComplete="name"
              {...register('name', {
                required: 'Name is required',
              })}
            />
          </Field>
          <Field
            error={
              // One span: Field's error box is flex, which would lay the
              // message and the CTA out as separate items and eat the
              // whitespace between them.
              errors.email && (
                <span>
                  {errors.email.message}
                  {errors.email.type === 'server' && <SalesCta />}
                </span>
              )
            }
          >
            <TextInput
              invalid={!!errors?.email}
              placeholder="Work Email"
              aria-label="Work Email"
              autoComplete="email"
              {...register('email', {
                required: 'Email is required',
                validate: {
                  emailSyntax: (email: string) =>
                    validateEmail(email) || 'Valid email is required',
                },
              })}
            />
          </Field>
          <PasswordInput register={register} watch={watch} errors={errors} />
        </Stack>
        <Button style={{ marginTop: '24px' }} disabled={loading} type="submit">
          {loading ? (
            <span className={uiStyles.buttonLoading}>
              Creating account...
              <Spinner size="xs" />
            </span>
          ) : (
            'Create account'
          )}
        </Button>
        {errorMessage && <p className={uiStyles.formError}>{errorMessage}</p>}
      </form>

      <LegalFooter action="Create account" />
    </Stack>
  );
};
